import { useState, useEffect } from "react";
import PropTypes from "prop-types";

// @mui material components
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

// @mui icons
import ImageIcon from "@mui/icons-material/Image";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import WorkspaceService from "services/workspace-service";
import LiveDemoRestriction from "layouts/components/Upgrade/liveDemo"; // [NEW] Live Demo Popup

function BrandingPackDialog({ open, onClose, onSuccess, initialData, isLiveDemo = false }) { // [NEW] Accept prop
    const [formData, setFormData] = useState({
        name: "",
        companyName: "",
        primaryColor: "#00A1A1",
        logoFile: null,
        heroFile: null,
    });
    const [logoPreview, setLogoPreview] = useState(null);
    const [heroPreview, setHeroPreview] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [showLiveDemoPopup, setShowLiveDemoPopup] = useState(false); // [NEW] Popup state

    useEffect(() => {
        if (open) {
            if (initialData) {
                setFormData({
                    name: initialData.name,
                    companyName: initialData.companyName || "",
                    primaryColor: initialData.primaryColor || "#00A1A1",
                    logoFile: null,
                    heroFile: null,
                });
                setLogoPreview(initialData.logoUrl);
                setHeroPreview(initialData.heroUrl);
            } else {
                resetForm();
            }
        }
    }, [open, initialData]);

    const resetForm = () => {
        setFormData({
            name: "",
            companyName: "",
            primaryColor: "#00A1A1",
            logoFile: null,
            heroFile: null,
        });
        setLogoPreview(null);
        setHeroPreview(null);
        setError("");
    };

    const handleFileChange = (e, type) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
            setError(`${type === "logo" ? "Logo" : "Hero"} image must be less than 5MB`);
            return;
        }

        // Validate file type
        if (!file.type.match(/image\/(png|jpeg|jpg)/)) {
            setError("Only PNG and JPG images are allowed");
            return;
        }

        setError("");

        if (type === "logo") {
            setFormData({ ...formData, logoFile: file });
            setLogoPreview(URL.createObjectURL(file));
        } else {
            setFormData({ ...formData, heroFile: file });
            setHeroPreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async () => {
        if (!formData.name.trim()) {
            setError("Pack name is required");
            return;
        }

        // [NEW] Live Demo Restriction
        if (isLiveDemo) {
            setShowLiveDemoPopup(true);
            return;
        }

        try {
            setSubmitting(true);
            setError("");

            const uploadData = new FormData();
            uploadData.append("name", formData.name);
            uploadData.append("companyName", formData.companyName);
            uploadData.append("primaryColor", formData.primaryColor);

            if (formData.logoFile) {
                uploadData.append("logo", formData.logoFile);
            }
            if (formData.heroFile) {
                uploadData.append("hero", formData.heroFile);
            }

            let result;
            if (initialData && initialData._id) {
                result = await WorkspaceService.updateWorkspaceBrandingPack(initialData._id, uploadData);
            } else {
                result = await WorkspaceService.createWorkspaceBrandingPack(uploadData);
            }

            if (onSuccess) {
                // Determine the correct object to pass back.
                // UserService usually returns the response object, but sometimes we want just the data.
                // Based on previous findings, createBrandingPack might return the response object.
                // Let's assume result.data or result is the pack.
                const pack = result.data || result;
                onSuccess(pack);
            }
            onClose();
        } catch (error) {
            console.error("Failed to save branding pack", error);
            setError(error.response?.data?.message || "Failed to save branding pack");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{initialData ? "Edit Branding Pack" : "Add Branding Pack"}</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <TextField
                    label="Pack Name"
                    fullWidth
                    margin="normal"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    helperText="e.g., 'Client A - Blue Theme'"
                />

                <TextField
                    label="Company Name"
                    fullWidth
                    margin="normal"
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                    helperText="Displayed on report cover page"
                />

                <TextField
                    label="Primary Color"
                    type="color"
                    fullWidth
                    margin="normal"
                    value={formData.primaryColor}
                    onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                />

                {/* Logo Upload */}
                <MDBox mt={3}>
                    <MDTypography variant="caption" fontWeight="bold" display="block" mb={1}>
                        Logo (PNG/JPG, max 5MB)
                    </MDTypography>
                    <MDButton variant="outlined" color="dark" component="label" startIcon={<ImageIcon />} fullWidth>
                        {formData.logoFile ? formData.logoFile.name : "Upload Logo"}
                        <input type="file" hidden accept="image/png,image/jpeg" onChange={(e) => handleFileChange(e, "logo")} />
                    </MDButton>
                    {logoPreview && (
                        <MDBox mt={1} textAlign="center">
                            <img
                                src={logoPreview}
                                alt="Logo Preview"
                                style={{ maxWidth: "150px", maxHeight: "100px", objectFit: "contain" }}
                            />
                        </MDBox>
                    )}
                </MDBox>

                {/* Hero Upload */}
                <MDBox mt={3}>
                    <MDTypography variant="caption" fontWeight="bold" display="block" mb={1}>
                        Hero Image (PNG/JPG, max 5MB)
                    </MDTypography>
                    <MDButton variant="outlined" color="dark" component="label" startIcon={<ImageIcon />} fullWidth>
                        {formData.heroFile ? formData.heroFile.name : "Upload Hero Image"}
                        <input type="file" hidden accept="image/png,image/jpeg" onChange={(e) => handleFileChange(e, "hero")} />
                    </MDButton>
                    {heroPreview && (
                        <MDBox mt={1} textAlign="center">
                            <img
                                src={heroPreview}
                                alt="Hero Preview"
                                style={{ maxWidth: "100%", maxHeight: "150px", objectFit: "cover" }}
                            />
                        </MDBox>
                    )}
                </MDBox>
            </DialogContent>

            <DialogActions>
                <MDButton onClick={onClose} color="secondary">
                    Cancel
                </MDButton>
                <MDButton onClick={handleSubmit} color="accent" disabled={submitting}>
                    {submitting ? "Saving..." : "Save"}
                </MDButton>
            </DialogActions>

            {/* [NEW] Live Demo Restriction Popup */}
            <LiveDemoRestriction
                open={showLiveDemoPopup}
                onClose={() => setShowLiveDemoPopup(false)}
                featureName="Adding Branding Pack"
            />
        </Dialog>
    );
}

BrandingPackDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
    initialData: PropTypes.object,
};

export default BrandingPackDialog;
